import React from 'react';

/**
 * Price cell that adapts when a promo is active.
 *
 *   <PriceWithPromo inrAmount={2999} currency={currency} promo={promo} size="xl" />
 *
 * • `promo` is the object returned by `usePromoPreview()`.
 * • When `promo.valid === false`, renders the plain currency-formatted price.
 * • When `promo.valid === true`, renders the discounted price, a strikethrough
 *   original, and a small "N% OFF" badge beside it.
 * • Uses theme CSS vars so it adapts to light/dark mode automatically.
 */
export default function PriceWithPromo({ inrAmount, currency, promo, size = 'xl', testid }) {
  const n = Number(inrAmount) || 0;
  const applied = promo?.valid ? promo.apply(n) : n;

  // Size → tailwind font classes
  const sizeClass = size === 'xl' ? 'text-3xl'
    : size === 'lg' ? 'text-2xl'
    : size === 'md' ? 'text-xl'
    : 'text-base';
  const strikeClass = size === 'xl' ? 'text-lg'
    : size === 'lg' ? 'text-base'
    : 'text-sm';

  if (!promo?.valid) {
    return (
      <span
        className={`${sizeClass} font-bold`}
        style={{ fontFamily: 'Manrope', color: 'var(--text)' }}
        data-testid={testid}
      >
        {currency.format(n)}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-2 flex-wrap" data-testid={testid}>
      <span
        className={`${sizeClass} font-bold`}
        style={{ fontFamily: 'Manrope', color: 'var(--text)' }}
      >
        {currency.format(applied)}
      </span>
      <span
        className={`${strikeClass} line-through`}
        style={{ color: 'var(--muted-fg)', opacity: 0.7 }}
        data-testid={testid ? `${testid}-original` : undefined}
      >
        {currency.format(n)}
      </span>
      <span
        className="text-xs font-bold px-2 py-1 rounded"
        style={{ background: 'var(--accent)', color: 'var(--accent-fg)', fontFamily: 'Manrope', letterSpacing: '0.04em' }}
        data-testid={testid ? `${testid}-badge` : 'promo-badge'}
      >
        {promo.percentOff}% OFF
      </span>
    </span>
  );
}
